import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  InputAdornment,
  Stack,
  styled,
  Table,
  TableCell,
  TableContainer as MuiTableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  TableBody,
  debounce,
  Dialog,
  Menu,
} from '@mui/material';
import { MoreHoriz, Search } from '@mui/icons-material';

import { groupsAtom } from '@/interfaces/site';
import { projectsAtom, selectedGroupIdAtom } from '@/interfaces/group';
import { deleteProjectAtom, selectedProjectIdAtom } from '@/interfaces/project';
import StarButton from '@/components/projects/StarButton';
import CreateProjectDialog from '@/components/projects/CreateProjectDialog';

import type { ChangeEvent, MouseEvent } from 'react';
import type { Project } from '@/@types/group';

const TableContainer = styled(MuiTableContainer)`
  background-color: white;
  & th {
    font-weight: bold;
  }
  & tbody td {
    padding: 8px 16px;
  }
`;

export default function ProjectList() {
  const { isLoading, data: groups } = useAtomValue(groupsAtom);
  const [selectedGroupId, setSelectedGroupId] = useAtom(selectedGroupIdAtom);
  const { data: projects, refetch } = useAtomValue(projectsAtom);
  const { mutate, data: deleteSuccess } = useAtomValue(deleteProjectAtom);
  const setSelectedProjectId = useSetAtom(selectedProjectIdAtom);

  const [query, setQuery] = useState('');
  const [tableData, setTableData] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project>();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const [dialog, setDialog] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    if (isLoading) return;
    setSelectedGroupId(groups.find((group) => group.isDefault)?.id ?? groups[0]?.id ?? '');
  }, [groups, isLoading, setSelectedGroupId]);
  useEffect(() => {
    if (!projects) return;
    setTableData(projects);
  }, [projects]);

  useEffect(() => {
    if (deleteSuccess === undefined) return;
    if (deleteSuccess) {
      window.alert(t('Projects.message.deleteSuccess'));
      refetch();
    } else {
      window.alert(t('Projects.message.deleteFailed'));
    }
  }, [deleteSuccess, refetch, t]);

  const filterProjects = useMemo(
    () =>
      debounce((query: string) => {
        if (query === '') {
          setTableData(projects);
        } else {
          setTableData(projects.filter((project) => project.name.toLowerCase().includes(query.toLowerCase())));
        }
      }, 500),
    [projects],
  );
  function onQueryChange(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    setQuery(value);
    filterProjects(value);
  }
  function onActionClick(e: MouseEvent<HTMLButtonElement>, project: Project) {
    setAnchorEl(e.currentTarget);
    setSelectedProject(project);
  }
  function onMenuClose() {
    setAnchorEl(null);
    setSelectedProject(null);
  }
  function onDeleteProjectClick() {
    const ok = window.confirm(
      `${t('Common.message.confirmDelete')}\n${t('Projects.label.projectName')}: ${selectedProject.name}`,
    );
    if (ok) {
      setAnchorEl(null);
      mutate(selectedProject.id);
    }
  }
  const onDialogClose = useCallback(() => {
    setDialog('');
    refetch();
  }, [refetch]);

  return (
    <>
      <Typography variant="h5" fontWeight="bold">
        {t('Common.label.projects')}
      </Typography>

      <Stack direction="row" gap={2}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('Common.label.group')}</InputLabel>
          <Select
            value={selectedGroupId}
            label={t('Common.label.group')}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            sx={{ bgcolor: 'white' }}
          >
            {!isLoading &&
              groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <TextField
          label={t('Common.label.search')}
          value={query}
          onChange={onQueryChange}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
          size="small"
          sx={{
            mr: 'auto',
            bgcolor: 'white',
          }}
        />
        <Button variant="contained" onClick={() => setDialog('CREATE_PROJECT')} sx={{ px: 3, fontSize: 16 }}>
          {t('Common.label.create')}
        </Button>
      </Stack>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 0 }}></TableCell>
              <TableCell>{t('Common.label.name')}</TableCell>
              <TableCell>{t('Common.label.devices')}</TableCell>
              <TableCell>{t('Common.label.action')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((project) => (
              <TableRow key={project.id}>
                <TableCell>
                  <StarButton projectId={project.id} projectName={project.name} priority={project.priority} />
                </TableCell>
                <TableCell>
                  <Link to={`/projects?projectId=${project.id}`} onClick={() => setSelectedProjectId(project.id)}>
                    {project.name}
                  </Link>
                </TableCell>
                <TableCell>{project.devices.length}</TableCell>
                <TableCell>
                  <IconButton onClick={(e) => onActionClick(e, project)} sx={{ p: 1 }}>
                    <MoreHoriz fontSize="medium" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={onMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <MenuItem onClick={onDeleteProjectClick}>{t('Projects.label.deleteProject')}</MenuItem>
            </Menu>
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog === 'CREATE_PROJECT'} onClose={onDialogClose}>
        <CreateProjectDialog onClose={onDialogClose} />
      </Dialog>
    </>
  );
}
